"use client";

import { useTranslation } from "react-i18next";
import { CountryISO2Mapping } from "@finbackoffice/fe-core";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import classNames from "classnames";
import { ITranslateProps, useDidUpdateEffect, useRuntimeConfig } from "@finbackoffice/site-core";
import {
    FC,
    RefObject,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    FieldError,
    FieldErrorsImpl,
    Merge,
    UseFormClearErrors,
    UseFormSetError,
} from "react-hook-form";
import { Country, TranslationScopes } from "@finbackoffice/enums";
import IntlTelInput, { CountryData } from "react-intl-tel-input";
import "react-intl-tel-input/dist/main.css";
import { ConfigContextV2 } from "contexts";
import Translate from "../translate/Translate";
import styles from "./phone-input.module.sass";

type IProps = {
    label?: string | ITranslateProps;
    defaultCountry?: string;
    enableAutoComplete?: boolean;
    enablePlaceholder?: boolean;
    disabled?: boolean;
    isCountryDisabled?: boolean;
    required?: boolean;
    value?: string;
    error?: FieldError | Merge<FieldError, FieldErrorsImpl<any>>;
    onChange: (val: string) => void;
    setError: UseFormSetError<any>;
    clearErrors: UseFormClearErrors<any>;
    wrapperClassname?: string;
    testId?: string;
};

const PhoneInput: FC<IProps> = ({
    enableAutoComplete,
    enablePlaceholder = true,
    disabled,
    isCountryDisabled = false,
    required,
    value,
    onChange,
    wrapperClassname,
    error,
    setError,
    clearErrors,
    label,
    testId,
}) => {
    const { t } = useTranslation();
    const COMMON_SITE_CONFIGS = useRuntimeConfig("COMMON_SITE_CONFIGS");
    const { siteClientCountry } = useContext(ConfigContextV2);
    const [number, setNumber] = useState(value);
    const [dialCode, setDialCode] = useState("");
    const defaultSupportedCountries = useMemo(
        () =>
            COMMON_SITE_CONFIGS.country.supported.map(
                (country) => CountryISO2Mapping[country as Country],
            ),
        [COMMON_SITE_CONFIGS.country.supported],
    );
    const [supportedCountries, setSupportedCountries] = useState(defaultSupportedCountries);

    const preferredCountries = useMemo(() => {
        const detectedCountryCode = siteClientCountry.isoAlpha2.toLowerCase();

        if (supportedCountries.length > 7) {
            if (supportedCountries.includes(detectedCountryCode)) {
                return [detectedCountryCode];
            } else {
                return [CountryISO2Mapping[COMMON_SITE_CONFIGS.country.default as Country]];
            }
        }

        return [];
    }, [COMMON_SITE_CONFIGS.country.default, siteClientCountry.isoAlpha2, supportedCountries]);

    const defaultCountryMemo = useMemo(() => {
        const defaultCountryCode =
            CountryISO2Mapping[COMMON_SITE_CONFIGS.country.default as Country];
        const detectedCountryCode = siteClientCountry.isoAlpha2;

        if (supportedCountries.includes(detectedCountryCode)) {
            return detectedCountryCode.toLowerCase();
        }

        return defaultCountryCode.toLowerCase();
    }, [COMMON_SITE_CONFIGS.country.default, siteClientCountry.isoAlpha2, supportedCountries]);
    const [defaultCountry] = useState(!value ? defaultCountryMemo : undefined);

    const telInputRef = useRef<IntlTelInput | null>(null);

    // if internal state was changed, change also external value
    useDidUpdateEffect(() => {
        onChange(`${dialCode ? `+${dialCode}` : ""}${number}`);
    }, [dialCode, number]);

    // if value was reset, reset also internal state
    useDidUpdateEffect(() => {
        const shouldResetToInitialValue = value && value !== `+${dialCode}${number}`;

        if (shouldResetToInitialValue) {
            telInputRef.current?.setNumber(value);
            setNumber(value.replace(`+${dialCode}`, ""));
        }
    }, [value]);

    useEffect(() => {
        if (telInputRef.current && value) {
            const selectedCountryData = telInputRef.current.selectedCountryData;
            const dialCode = selectedCountryData?.dialCode;

            if (!dialCode) {
                const detectedPhone = parsePhoneNumberFromString(value);

                if (detectedPhone?.country) {
                    setSupportedCountries((state) => [...state, detectedPhone.country as string]);

                    telInputRef.current.tempCountry = detectedPhone.country.toLowerCase();
                } else {
                    telInputRef.current.tempCountry = "unknown";
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useDidUpdateEffect(() => {
        if (telInputRef.current) {
            telInputRef.current.processAllCountries();
            telInputRef.current.processCountryCodes();
        }
    }, [supportedCountries]);

    const isSelectedCountrySupported = useCallback(
        (telInputRef: RefObject<IntlTelInput | null>) =>
            telInputRef.current &&
            telInputRef.current.selectedCountryData?.iso2 &&
            defaultSupportedCountries.includes(
                telInputRef.current.selectedCountryData.iso2.toLowerCase(),
            ),
        [defaultSupportedCountries],
    );

    const onPhoneNumberChangeHandler = useCallback(
        (
            valid: boolean,
            value: string,
            country: CountryData,
            _fullNumber: string,
            onChange: (...event: any[]) => void,
        ) => {
            const newValue: string = value;
            const isValid = COMMON_SITE_CONFIGS.phoneValidationDigits
                ? value.length === COMMON_SITE_CONFIGS.phoneValidationDigits
                : valid;

            if (isValid) {
                if (!isSelectedCountrySupported(telInputRef)) {
                    setError("root.apiError", {
                        type: "notValid",
                        message: t("validation_err_unsupportedCountryCode"),
                    });
                } else {
                    clearErrors("root.apiError");
                }
            } else {
                if (!required && !value.trim()) {
                    clearErrors("root.apiError");
                } else if (!error) {
                    setError("root.apiError", {
                        type: "notValid",
                        message: t("validation_err_notValid"),
                    });
                }
            }

            setDialCode(country.dialCode ?? "");

            onChange(newValue);
        },
        [
            COMMON_SITE_CONFIGS.phoneValidationDigits,
            clearErrors,
            error,
            isSelectedCountrySupported,
            required,
            setError,
            t,
        ],
    );

    const onPhoneNumberFocusHandler = (
        valid: boolean,
        value: string,
        _country: CountryData,
        _fullNumber: any,
        _onChange: (val: string) => void,
    ) => {
        const newValue: string = value;
        if (!newValue) {
            if (!valid) {
                setError("root.apiError", {
                    type: "notValid",
                    message: t("validation_err_notValid"),
                });
            }
        }
    };

    const onPhoneNumberBlurHandler = (
        valid: boolean,
        _value: string,
        country: CountryData,
        fullNumber: string,
        onChange: (val: string) => void,
    ) => {
        if (valid) {
            const formatedNumber = fullNumber.replaceAll("-", "").replaceAll(" ", "").trim();

            onChange(formatedNumber.replace(`+${country.dialCode}`, ""));
        }
    };

    const handleFlagChange = (
        _currentNumber: string,
        selectedCountryData: CountryData,
        _fullNumber: string,
        isValid: boolean,
    ) => {
        if (isValid) {
            setDialCode(selectedCountryData.dialCode ?? "");

            if (!isSelectedCountrySupported(telInputRef)) {
                setError("root.apiError", {
                    type: "notValid",
                    message: t("validation_err_unsupportedCountryCode"),
                });
            } else {
                clearErrors("root.apiError");
            }
        } else if (value) {
            setError("root.apiError", {
                type: "notValid",
                message: t("validation_err_notValid"),
            });
        }
    };

    return (
        <div
            data-testid={testId}
            className={classNames(
                styles.phoneInput,
                supportedCountries.length === 1 && styles.oneCountry,
                wrapperClassname,
            )}>
            {label && (
                <span className="intl-tel-input-label">
                    <Translate
                        tid={typeof label === "string" ? label : (label as ITranslateProps).tid}
                        namespace={
                            typeof label !== "string"
                                ? (label as ITranslateProps).namespace
                                : TranslationScopes.Common
                        }
                    />
                    :
                </span>
            )}
            <IntlTelInput
                ref={telInputRef}
                allowDropdown={!isCountryDisabled && supportedCountries.length > 1}
                fieldName="phone"
                autoComplete={enableAutoComplete ? "on" : "off"}
                disabled={disabled}
                containerClassName={classNames(
                    "intl-tel-input",
                    required && "required",
                    error && styles.fieldError,
                )}
                inputClassName="phone-widget"
                nationalMode={false}
                onSelectFlag={handleFlagChange}
                value={number}
                formatOnInit={false}
                onlyCountries={supportedCountries}
                defaultCountry={defaultCountry}
                preferredCountries={preferredCountries}
                separateDialCode
                onPhoneNumberBlur={(valid, val, country, fullNumber) => {
                    onPhoneNumberBlurHandler(valid, val, country, fullNumber, setNumber);
                }}
                onPhoneNumberFocus={(valid, val, country, fullNumber) => {
                    onPhoneNumberFocusHandler(valid, val, country, fullNumber, setNumber);
                }}
                onPhoneNumberChange={(valid, val, country, fullNumber) => {
                    onPhoneNumberChangeHandler(valid, val, country, fullNumber, setNumber);
                }}
                autoPlaceholder={enablePlaceholder}
            />

            {error && (
                <div className={classNames(styles.error, "field-error")}>
                    {error.message?.toString()}
                </div>
            )}
        </div>
    );
};

export default PhoneInput;
